// src/actions/estimate.js
import axios from "../helpers/axios";
import { socket } from "../containers/ChatPage/socket";
import { estimateConstants as C } from "./constants";

// ===== helpers =====
const normalizeList = (data) => {
  const items = data?.items ?? data?.docs ?? data?.data ?? [];
  const total = data?.total ?? data?.totalDocs ?? (Array.isArray(items) ? items.length : 0);
  const page = data?.page ?? data?.pageNum ?? 1;
  const limit = data?.limit ?? data?.pageSize ?? 50;
  return { items, total, page, limit };
};
const pickDoc = (payload) => payload?.doc ?? payload?.item ?? payload?.data ?? payload;

const toNumOr0 = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toNumOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const roomKey = (r, idx) => r?._id || r?.id || r?.uid || r?.key || String(idx);

const normalizeRoom = (next, base) => {
  const n = next || {};
  const b = base || {};

  const merged = { ...b, ...n };

  const ceilingType =
    n?.ceilingType !== undefined && n?.ceilingType !== null && n?.ceilingType !== ""
      ? n.ceilingType
      : b?.ceilingType;

  const ceilingDropMmRaw =
    n?.ceilingDropMm !== undefined && n?.ceilingDropMm !== null && n?.ceilingDropMm !== ""
      ? n.ceilingDropMm
      : b?.ceilingDropMm;

  const ceilingAreaM2OverrideRaw =
    n?.ceilingAreaM2Override !== undefined && n?.ceilingAreaM2Override !== null && n?.ceilingAreaM2Override !== ""
      ? n.ceilingAreaM2Override
      : b?.ceilingAreaM2Override;

  const ceilingLights =
    Array.isArray(n?.ceilingLights) ? n.ceilingLights : (Array.isArray(b?.ceilingLights) ? b.ceilingLights : []);

  merged.ceilingType = ceilingType || merged?.ceilingType;
  merged.ceilingDropMm = toNumOr0(ceilingDropMmRaw);
  merged.ceilingAreaM2Override = toNumOrNull(ceilingAreaM2OverrideRaw);
  merged.ceilingLights = ceilingLights;

  return merged;
};

const mergeRooms = (baseRooms, nextRooms) => {
  const baseArr = Array.isArray(baseRooms) ? baseRooms : [];
  const nextArr = Array.isArray(nextRooms) ? nextRooms : [];

  if (!nextArr.length) return nextArr;

  const map = new Map();
  baseArr.forEach((r, i) => map.set(roomKey(r, i), r));

  return nextArr.map((r, i) => {
    const key = roomKey(r, i);
    const base = map.get(key);
    return normalizeRoom(r, base);
  });
};

const selectEstimateDocFromState = (state, estimateId) => {
  if (!state || !estimateId) return null;

  const direct = [
    state?.estimate?.local,
    state?.estimate?.current,
    state?.estimate?.doc,
    state?.estimates?.local,
    state?.estimates?.current,
    state?.estimateEditor?.local,
    state?.estimateEditor?.current,
  ].filter(Boolean);

  for (const d of direct) {
    const id = d?._id || d?.id;
    if (id === estimateId) return d;
  }

  const lists = [
    state?.estimate?.list?.items,
    state?.estimate?.items,
    state?.estimates?.list?.items,
    state?.estimates?.items,
  ].filter((x) => Array.isArray(x));

  for (const arr of lists) {
    const found = arr.find((x) => (x?._id || x?.id) === estimateId);
    if (found) return found;
  }

  return null;
};

// ===== Local/UI =====
export const setLocalEstimate = (payload) => ({ type: C.SET_LOCAL, payload });
export const setLoading = (v) => ({ type: C.SET_LOADING, payload: v });

// ===== List =====
export const listEstimates = (params = {}) => {
  return async (dispatch) => {
    dispatch({ type: C.LIST_REQUEST, params });
    try {
      const qs = new URLSearchParams(params).toString();
      const res = await axios.get(`/estimates${qs ? `?${qs}` : ""}`);
      if (res.status === 200 && res.data) {
        const payload = normalizeList(res.data);
        dispatch({ type: C.LIST_SUCCESS, payload });
      } else {
        dispatch({ type: C.LIST_FAILURE, error: `HTTP ${res.status}` });
      }
    } catch (e) {
      dispatch({ type: C.LIST_FAILURE, error: e?.message || "LIST_FAILED" });
    }
  };
};

// ===== Get one =====
export const getEstimate = (estimateId) => {
  return async (dispatch) => {
    dispatch({ type: C.GET_REQUEST, estimateId });
    try {
      const res = await axios.get(`/estimates/${estimateId}`);
      if (res.status === 200 && res.data) {
        dispatch({ type: C.GET_SUCCESS, payload: pickDoc(res.data) });
      } else {
        dispatch({ type: C.GET_FAILURE, error: `HTTP ${res.status}` });
      }
    } catch (e) {
      dispatch({ type: C.GET_FAILURE, error: e?.message || "GET_FAILED" });
    }
  };
};

// ===== Create =====
export const createEstimate = (payload) => {
  return async (dispatch) => {
    dispatch({ type: C.CREATE_REQUEST });
    try {
      const res = await axios.post(`/estimates`, payload);
      if (res.status === 201 && res.data) {
        const doc = pickDoc(res.data);
        dispatch({ type: C.CREATE_SUCCESS, payload: doc });
        return doc;
      }
      throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      dispatch({ type: C.CREATE_FAILURE, error: e?.message || "CREATE_FAILED" });
      return null;
    }
  };
};

// ===== Update (REST) =====
export const updateEstimate = (estimateId, patch) => {
  return async (dispatch) => {
    dispatch({ type: C.UPDATE_REQUEST, estimateId, patch });
    try {
      const res = await axios.patch(`/estimates/${estimateId}`, patch);
      if (res.status === 200 && res.data) {
        const doc = pickDoc(res.data);
        dispatch({ type: C.UPDATE_SUCCESS, payload: doc });
        return doc;
      }
      throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      dispatch({ type: C.UPDATE_FAILURE, error: e?.message || "UPDATE_FAILED" });
      return null;
    }
  };
};

// ===== Delete =====
export const deleteEstimate = (estimateId) => {
  return async (dispatch) => {
    dispatch({ type: C.DELETE_REQUEST, estimateId });
    try {
      const res = await axios.delete(`/estimates/${estimateId}`);
      if (res.status === 200) {
        dispatch({ type: C.DELETE_SUCCESS, payload: { estimateId } });
      } else {
        dispatch({ type: C.DELETE_FAILURE, error: `HTTP ${res.status}` });
      }
    } catch (e) {
      dispatch({ type: C.DELETE_FAILURE, error: e?.message || "DELETE_FAILED" });
    }
  };
};

// ===== Finalize (PDF) =====
export const finalizeEstimate = (estimateId, userId) => {
  return async (dispatch) => {
    dispatch({ type: C.FINALIZE_REQUEST, estimateId, userId });
    try {
      const res = await axios.post(`/estimates/${estimateId}/finalize`, { userId });
      if (res.status === 200 && res.data?.url) {
        dispatch({ type: C.FINALIZE_SUCCESS, payload: res.data });
        return res.data;
      }
      throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      dispatch({ type: C.FINALIZE_FAILURE, error: e?.message || "FINALIZE_FAILED" });
      return null;
    }
  };
};

// ===== Duplicate =====
export const duplicateEstimate = (estimateId, userId) => {
  return async (dispatch) => {
    dispatch({ type: C.DUPLICATE_REQUEST, estimateId, userId });
    try {
      const res = await axios.post(`/estimates/${estimateId}/duplicate`, { userId });
      if (res.status === 201 && res.data) {
        const doc = pickDoc(res.data);
        dispatch({ type: C.DUPLICATE_SUCCESS, payload: doc });
        return doc;
      }
      throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      dispatch({ type: C.DUPLICATE_FAILURE, error: e?.message || "DUPLICATE_FAILED" });
      return null;
    }
  };
};

// ===== Templates =====
export const listEstimateTemplates = () => {
  return async (dispatch) => {
    dispatch({ type: C.TEMPLATES_REQUEST });
    try {
      const res = await axios.get(`/estimate-templates`);
      if (res.status === 200 && res.data) {
        const items = Array.isArray(res.data?.items) ? res.data.items : (res.data?.data ?? []);
        dispatch({ type: C.TEMPLATES_SUCCESS, payload: items });
      } else {
        dispatch({ type: C.TEMPLATES_FAILURE, error: `HTTP ${res.status}` });
      }
    } catch (e) {
      dispatch({ type: C.TEMPLATES_FAILURE, error: e?.message || "TEMPLATES_FAILED" });
    }
  };
};

export const applyTemplateToEstimate = (estimateId, templateId, userId) => {
  return async (dispatch) => {
    dispatch({ type: C.APPLY_TEMPLATE_REQUEST, estimateId, templateId, userId });
    try {
      const res = await axios.post(`/estimates/${estimateId}/apply-template`, { templateId, userId });
      if (res.status === 200 && res.data) {
        const doc = pickDoc(res.data);
        dispatch({ type: C.APPLY_TEMPLATE_SUCCESS, payload: doc });
        return doc;
      }
      throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      dispatch({ type: C.APPLY_TEMPLATE_FAILURE, error: e?.message || "APPLY_TEMPLATE_FAILED" });
      return null;
    }
  };
};

// ===== Sockets (collab) =====
export const subscribeEstimateSockets = (estimateId, userId) => {
  return (dispatch) => {
    socket.emit("estimate:join", { estimateId, userId }, () => { });
    const onSnapshot = (payload) => dispatch({ type: C.SOCKET_SNAPSHOT, payload });
    const onUpdated = (payload) => dispatch({ type: C.SOCKET_UPDATED, payload });
    const onLocked = (payload) => dispatch({ type: C.SOCKET_LOCKED, payload });
    const onUnlocked = (payload) => dispatch({ type: C.SOCKET_UNLOCKED, payload });
    const onPresence = (payload) => dispatch({ type: C.SOCKET_PRESENCE, payload });

    socket.on("estimate:snapshot", onSnapshot);
    socket.on("estimate:updated", onUpdated);
    socket.on("estimate:locked", onLocked);
    socket.on("estimate:unlocked", onUnlocked);
    socket.on("estimate:presence", onPresence);

    return () => {
      socket.off("estimate:snapshot", onSnapshot);
      socket.off("estimate:updated", onUpdated);
      socket.off("estimate:locked", onLocked);
      socket.off("estimate:unlocked", onUnlocked);
      socket.off("estimate:presence", onPresence);
      socket.emit("estimate:leave", { estimateId, userId });
    };
  };
};

export const lockField = (estimateId, field, userId) => () =>
  socket.emit("estimate:lock", { estimateId, field, userId });

export const unlockField = (estimateId, field) => () =>
  socket.emit("estimate:unlock", { estimateId, field });

export const updateEstimateSocket = (estimateId, patch, userId) => {
  return (dispatch, getState) =>
    new Promise((resolve) => {
      const state = typeof getState === "function" ? getState() : null;
      const baseDoc = selectEstimateDocFromState(state, estimateId);

      const nextRooms = Array.isArray(patch?.rooms) ? patch.rooms : null;
      const baseRooms = Array.isArray(baseDoc?.rooms) ? baseDoc.rooms : null;

      const normalizedPatch =
        nextRooms
          ? { ...patch, rooms: mergeRooms(baseRooms, nextRooms) }
          : patch;

      const stamped = {
        ...normalizedPatch,
        _client: userId,
        _ts: normalizedPatch?._ts || Date.now(),
        _rev: (normalizedPatch?._rev || 0),
      };

      console.log("[updateEstimateSocket] emit", {
        estimateId,
        userId,
        rooms: stamped?.rooms?.length,
        materials: stamped?.materials?.length,
        works: stamped?.works?.length,
      });

      let done = false;
      const finish = (ack) => {
        if (done) return;
        done = true;
        resolve(ack);
      };
      const TRACE = true;

      if (TRACE) {
        const r0 = Array.isArray(stamped?.rooms) ? stamped.rooms[0] : null;
        console.log("[updateEstimateSocket] OUT patch.keys =", Object.keys(stamped || {}));
        if (r0) {
          console.log("[updateEstimateSocket] OUT r0.ceilingType =", r0?.ceilingType);
          console.log("[updateEstimateSocket] OUT r0.ceilingDropMm =", r0?.ceilingDropMm);
          console.log("[updateEstimateSocket] OUT r0.ceilingAreaM2Override =", r0?.ceilingAreaM2Override);
          console.log("[updateEstimateSocket] OUT r0.ceilingLights.len =", Array.isArray(r0?.ceilingLights) ? r0.ceilingLights.length : "na");
        }
      }

      const timer = setTimeout(() => finish(null), 8000);

      socket.emit("estimate:update", { estimateId, patch: stamped, userId }, (ack) => {
        clearTimeout(timer);

        if (TRACE) {
          const d0 = Array.isArray(ack?.doc?.rooms) ? ack.doc.rooms[0] : null;
          console.log("[updateEstimateSocket] ACK ok =", ack?.ok, "err =", ack?.error);
          if (d0) {
            console.log("[updateEstimateSocket] ACK doc.r0.ceilingDropMm =", d0?.ceilingDropMm);
            console.log("[updateEstimateSocket] ACK doc.r0.ceilingLights.len =", Array.isArray(d0?.ceilingLights) ? d0.ceilingLights.length : "na");
          }
        }

        finish(ack);
      });
    });
};

export const deleteEstimatePdf = (estimateId, fileRef, removeFile = true) => {
  return async (dispatch) => {
    dispatch({ type: C.PDF_DELETE_REQUEST });
    try {
      const res = await axios.delete(`/estimates/${estimateId}/pdf`, {
        data: { file: fileRef, removeFile }
      });
      if (res.status === 200 && res.data?.ok) {
        dispatch({ type: C.PDF_DELETE_SUCCESS, payload: { estimateId, file: fileRef } });
        return res.data;
      }
      throw new Error();
    } catch {
      dispatch({ type: C.PDF_DELETE_FAILURE });
      return null;
    }
  };
};
